

import { Component, Vue, Watch } from "vue-property-decorator"
import { Getter, namespace, State } from "vuex-class"

import cloneDeep from "lodash/cloneDeep"

import { Area, AreaDetails, DataDefinition, DataSource, TreeNode } from "@/types"

import api from "@/services/api"

import { BModal } from "bootstrap-vue"
import vSelect from "vue-select"

import VuePerfectScrollbar from "vue-perfect-scrollbar"

import CadenceSchedules from "@/components/Config/Schedules/CadenceSchedules.vue"
import CssSetup from "@/components/Config/CssSetup.vue"
import DataModal from "@/components/Config/DataModal.vue"
import DefinitionSetup from "@/components/Config/Definitions/DefinitionSetup.vue"
import FieldSetup from "@/components/Config/Fields/FieldSetup.vue"
import PageWrapper from "@/components/New/Page/PageWrapper.vue"
import RuleSetup from "@/components/Config/Rules/RuleSetup.vue"
import ScrollTabs from "@/components/Controls/Tabs/ScrollTabs.vue"
import ScrollTab from "@/components/Controls/Tabs/ScrollTab.vue"
import SourceSetup from "@/components/Config/Sources/SourceSetup.vue"
import SourceSearch from "@/components/Config/Sources/SourceSearch.vue"
import TagSetup from "@/components/Config/Tags/TagSetup.vue"
import TemplateSetup from "@/components/Config/Template/TemplateSetup.vue"
import IntegrationSetup from "@/components/Config/Integrations/IntegrationSetup.vue"
import ViewSetup from "@/components/Config/Views/ViewSetup.vue"
import DomainSetup from "@/components/Config/Domain/DomainSetup.vue"
import TenantSettingsPage from "@/components/Config/TenantSettings/TenantSettingsPage.vue"
import { CurrentUser } from "@/types/user"
import FormatSetup from "@/components/Config/Formats/FormatSetup.vue"
import { TenantFeatures } from "@/types/tenantFeatures"

const setup = namespace("setup")
const caseStore = namespace("cases")

interface FilteredDataSourcesResult {
	filtered: DataSource[]
	isSelectedSourceAppended: boolean
}

@Component({
	components: {
		"page": PageWrapper,
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"field-setup": FieldSetup,
		"template-setup": TemplateSetup,
		"view-setup": ViewSetup,
		"definition-setup": DefinitionSetup,
		"source-setup": SourceSetup,
		"source-search": SourceSearch,
		"rule-setup": RuleSetup,
		"data-modal": DataModal,
		"tag-setup": TagSetup,
		"cadence-schedules": CadenceSchedules,
		"scroll-tab": ScrollTab,
		"scroll-tabs": ScrollTabs,
		"css-setup": CssSetup,
		"integration-setup": IntegrationSetup,
		"domain-setup": DomainSetup,
		"tenant-settings": TenantSettingsPage,
		"format-setup": FormatSetup
	}
})
export default class Configuration extends Vue {
	$refs!: {
		dataModal: BModal
	}

	@State
	me!: CurrentUser | null

	@Getter("getIsSuiteEnabled") isSuiteEnabled! : boolean;

	@Getter isFeatureEnabled!: (featureKey: TenantFeatures) => boolean;

	@setup.Action
	public loadTeams!: () => Promise<void>

	@setup.Action
	public loadDataSources!: () => Promise<void>

	@setup.Action
	public loadDefinitions!: () => Promise<void>

	@setup.Getter("getTeams")
	public teams!: any[]

	@setup.Getter("getDefinitions")
	public definitions!: DataDefinition[]

	@setup.State("dataSources")
	public dataSources!: DataSource[]

	@caseStore.Action
	public detailsLoad!: (refreshData: boolean) => void

	@caseStore.State("areas")
	public areas!: TreeNode<Area>[]

	public loading: boolean = true
	private maxVisibleDataSources: number = 200

	private filter: string = ""
	private debouncedFilter: string = ""
	private debounceTimeout: NodeJS.Timeout | null = null
	private debounceFilterMs: number = 300

	private selectedDataSource: DataSource | null = null

	public async mounted () {
		await Promise.all([
			this.loadTeams(),
			this.loadDataSources(),
			this.loadDefinitions()
		])

		this.loading = false
	}

	@Watch("filter")
	private onFilterChanged (newVal: string) {
		if (this.debounceTimeout) {
			clearTimeout(this.debounceTimeout)
		}

		this.debounceTimeout = setTimeout(() => {
			this.debouncedFilter = newVal
		}, this.debounceFilterMs)
	}

	private get permittedDataSources (): DataSource[] {
		if (!this.me) {
			return []
		}

		if (this.me.editData) {
			return this.dataSources || []
		}

		if (this.me.teams?.length) {
			const teamIds = this.me.teams.map(t => t.id)
			return (this.dataSources || []).filter(ds =>
				ds.editPermissions?.some(ep => teamIds.includes(ep))
			)
		}
		return []
	}

	private get matchingDataSources (): DataSource[] {
		let filtered = this.permittedDataSources

		if (this.debouncedFilter) {
			filtered = filtered.filter(ds =>
				ds.title.toLowerCase().includes(this.debouncedFilter.trim().toLowerCase())
			)
		}

		return filtered
	}

	private get filteredDataSources (): FilteredDataSourcesResult {
		const filtered = this.matchingDataSources.slice(0, this.maxVisibleDataSources)
		const selected = this.selectedDataSource
		const inFiltered = selected && filtered.some(ds => ds.id === selected.id)

		return {
			// Append selected source if missing so Scroll Tab can render it. isSelectedSourceAppended controls visibility
			filtered: inFiltered ? filtered : selected ? filtered.concat(selected) : filtered,
			isSelectedSourceAppended: !!selected && !inFiltered
		}
	}

	private get hasExcessDataSources (): boolean {
		return this.matchingDataSources.length > this.maxVisibleDataSources
	}

	private get dataSourceFilterPlaceholder (): string {
		const matchingWord = this.debouncedFilter ? "matching " : ""
		return `Displaying ${this.maxVisibleDataSources} of ${this.matchingDataSources.length} ${matchingWord}data sources. Filter to show more.`
	}

	public get columns (): any[] {
		return [
			{
				key: "title",
				label: "Title",
				sortable: true,
				errors: (item: any) => item.title ? null : "Please enter a title"
			},
			{
				key: "definitionTitle",
				label: "Item Type",
				sortable: true,
				dataType: "list",
				valueColumn: "definitionId",
				options: this.definitions,
				description: "Type of data in this list. Leave empty for a simple list."
			},
			{
				key: "includedLists",
				label: "Included Lists",
				dataType: "list-multiple",
				valueColumn: "includedLists",
				options: this.permittedDataSources,
				limitOptions: true,
				description: "Include items from list in this one. It won't include any additional lists included to these",
				errors: (item: any) => this.checkForSelfDataSource(item)
			},
			{
				key: "editPermissions",
				label: "Edit Permissions",
				dataType: "list-multiple",
				valueColumn: "editPermissions",
				options: this.teams,
				description: "Which teams are able to edit items in this data source"
			},
			{
				key: "createdBy.name",
				label: "Created By",
				inModal: () => false,
				hasTemplate: true
			},
			{
				key: "lastUpdated",
				label: "Last Updated",
				inModal: () => false,
				hasTemplate: true
			},
			{
				key: "buttons",
				label: "",
				inModal: () => false,
				hasTemplate: true
			}
		]
	};

	public get areaDefinitionId (): string | null {
		const definition = this.definitions.find(d => d.specialType === "area")

		return definition ? definition.id! : null
	}

	public get areaSource () {
		return {
			id: "areas",
			title: "Areas",
			extraFields: [
				{ key: "title", label: "Title", readonly: this.isSuiteEnabled, errors: (item: { title: string }) => (!item.title || item.title.trim() === "") ? "Please enter a title" : null },
				{ key: "address", label: "Address", dataType: "text-area", readonly: this.isSuiteEnabled },
				{ key: "location", label: "Location", dataType: "map-marker", readonly: this.isSuiteEnabled }],
			noEdit: true,
			hideUpload: true,
			definitionId: this.definitions ? this.areaDefinitionId : null,
			createItemModalTitle: "Create Area",
			getItems: (pageNumber: number, pageSize: number, searchTerm: string) => api.paginatedAreas(pageNumber, pageSize, searchTerm),
			itemDetails: (item: Area) => api.area(item.id),
			save: (newLocation: AreaDetails) => this.trySaveArea(newLocation),
			delete: (deleteLocation: AreaDetails) => this.tryDeleteArea(deleteLocation),
			treeLayout: true,
			treeItemSelectedModalTitle: "Area",
			treeCreateItemModalTitle: "Create sub-area"
		}
	}

	async trySaveArea (newArea: AreaDetails) {
		try {
			await api.areaSave(newArea)
		} catch (err) {
			this.$bvToast.toast(err, {
				title: `Error saving Area: ${newArea.title}`,
				variant: "danger",
				solid: true,
				autoHideDelay: 10000
			})
			if (!newArea.id) {
				return null
			}
			return api.area(newArea.id)
		}
	}

	async tryDeleteArea (area: AreaDetails) {
		try {
			await api.areaDelete(area)
		} catch (err) {
			this.$bvToast.toast(err, {
				title: `Error deleting Area: ${area.title}`,
				variant: "danger",
				solid: true,
				autoHideDelay: 10000
			})
			throw err
		}
	}

	async save (source: any) {
		source.includedLists = source.includedLists
			.map((listId: any) => listId.id
				? listId
				: this.permittedDataSources.find((included: any) => included.id === listId))

		await api.dataSourceSave(source)

		await this.loadDataSources()
	}

	async dataSourceDeleteDetails (dataSource: any) {
		if (!dataSource!.id) {
			return
		}

		return api.dataSourceUsage(dataSource)
	}

	createNewSource () {
		this.$refs.dataModal.open({
			includedLists: []
		})
	}

	editSource (dataSource: any) {
		this.$refs.dataModal.open(cloneDeep(dataSource))
	}

	async deleteSource (dataSource: any) {
		await api.dataSourceDelete(dataSource)

		await this.loadDataSources()
	}

	private checkForSelfDataSource (item: any): string | null {
		if (item.includedLists.some((i: any) => i === item.id)) {
			return `Data source cannot be added to itself: ${item.title}`
		}

		return null
	}

	private get isPdfTemplateEnabled () {
		return this.isFeatureEnabled(TenantFeatures.PdfTemplates)
	}
}

